import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatErr } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined); // undefined = loading
  const [error, setError] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    // Skip /me on OAuth callback and on /portal/* routes (portal uses its own auth)
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setUser(null);
      return;
    }
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/portal")) {
      setUser(null);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    setError(null);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return data;
    } catch (e) {
      const msg = formatErr(e);
      setError(msg);
      throw new Error(msg);
    }
  };

  const register = async (email, password, name) => {
    setError(null);
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      setUser(data);
      return data;
    } catch (e) {
      const msg = formatErr(e);
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
  };

  const setUserFromOAuth = (u) => setUser(u);

  return (
    <AuthContext.Provider value={{ user, error, login, register, logout, checkAuth, setUserFromOAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
